const strEnum = <T extends string>(o: T[]): { [K in T]: K } => {
  return o.reduce((res, key) => {
    res[key] = key
    return res
  }, Object.create(null))
}

// Status enum from acquisition.py (renamed from GCStatus)
export const STATUSES = ['imported', 'reported', 'invalid'] as const
export const AcqusitionStatusType = strEnum([...STATUSES])
export type AcqusitionStatusValue = (typeof STATUSES)[number]

// Consent enum from acquisition.py
export const CONSTENT = ['accepted', 'rejected'] as const
export const AcqusitionConsentType = strEnum([...CONSTENT])
export type AcqusitionConsentValue = (typeof CONSTENT)[number]

// TumorPresence enum from annotation.py (updated to match backend)
export const TUMOR_PRESENCES = ['yes', 'no'] as const
export const TumorPresenceType = strEnum([...TUMOR_PRESENCES])
export type TumorPresence = (typeof TUMOR_PRESENCES)[number]

// Annotation type enum (manual, pathology, reset annotations)
export const ANNOTATION_TYPES = ['manual', 'pathology', 'reset'] as const
export const AnnotationTypeEnum = strEnum([...ANNOTATION_TYPES])
export type AnnotationTypeValue = (typeof ANNOTATION_TYPES)[number]

// Annotation status enum
export const ANNOTATION_STATUSES = ['incomplete', 'partial', 'complete'] as const
export const AnnotationStatusEnum = strEnum([...ANNOTATION_STATUSES])
export type AnnotationStatusValue = (typeof ANNOTATION_STATUSES)[number]

// Acquisition report status enum (from acquisition_report.py). `pending` also covers
// an acquisition that has no report at all — the API never reports its absence as an
// error.
export const REPORT_STATUSES = ['pending', 'running', 'ready', 'failed', 'unsupported'] as const
export const ReportStatusEnum = strEnum([...REPORT_STATUSES])
export type ReportStatusValue = (typeof REPORT_STATUSES)[number]

// SNS Message type enum
export const SNS_MESSAGE_TYPES = ['SubscriptionConfirmation', 'Notification', 'UnsubscribeConfirmation'] as const
export const SNSMessageTypeEnum = strEnum([...SNS_MESSAGE_TYPES])
export type SNSMessageTypeValue = (typeof SNS_MESSAGE_TYPES)[number]

// S3 Sync status enum
export const S3_SYNC_STATUSES = ['idle', 'syncing', 'completed', 'locked'] as const
export const S3SyncStatusEnum = strEnum([...S3_SYNC_STATUSES])
export type S3SyncStatusValue = (typeof S3_SYNC_STATUSES)[number]

// User roles enum
export const USER_ROLES = ['superadmin', 'admin', 'user', 'demo'] as const
export const UserRoleEnum = strEnum([...USER_ROLES])
export type UserRoleValue = (typeof USER_ROLES)[number]

// Specimen track enum (for annotation.py)
export const SPECIMEN_TRACKS = ['other', 'intraoperative_rfs'] as const
export const SpecimenTrackEnum = strEnum([...SPECIMEN_TRACKS])
export type SpecimenTrack = (typeof SPECIMEN_TRACKS)[number]

// Annotation field names - data fields only (excludes metadata like id, type, created)
export const ANNOTATION_DATA_FIELDS = [
  'specimen_track',
  'tissue_topography',
  'neoadjuvant',
  'other_malignancy',
  'other_macro_assessment',
  'intraoperative_rfs_rfs_preliminary_tumor_presence',
  'intraoperative_rfs_rfs_preliminary_intraoperative_report',
  'intraoperative_rfs_rfs_final_assessment_ffpe',
  'intraoperative_rfs_rfs_final_malignancy',
  'intraoperative_rfs_rfs_final_report',
  'case',
] as const

export const AnnotationDataFieldEnum = strEnum([...ANNOTATION_DATA_FIELDS])
export type AnnotationDataFieldValue = (typeof ANNOTATION_DATA_FIELDS)[number]

// Annotation field names - all fields including metadata (for comprehensive coverage)
export const ANNOTATION_ALL_FIELDS = ['id', 'type', 'created', 'created_by', ...ANNOTATION_DATA_FIELDS] as const

export const AnnotationFieldEnum = strEnum([...ANNOTATION_ALL_FIELDS])
export type AnnotationFieldValue = (typeof ANNOTATION_ALL_FIELDS)[number]

// Track-specific field groupings (optional, for semantic clarity)
export const ANNOTATION_SHARED_FIELDS = ['tissue_topography', 'neoadjuvant'] as const

export const ANNOTATION_OTHER_TRACK_FIELDS = ['other_malignancy', 'other_macro_assessment'] as const

export const ANNOTATION_RFS_TRACK_FIELDS = [
  'intraoperative_rfs_rfs_preliminary_tumor_presence',
  'intraoperative_rfs_rfs_preliminary_intraoperative_report',
  'intraoperative_rfs_rfs_final_assessment_ffpe',
  'intraoperative_rfs_rfs_final_malignancy',
  'intraoperative_rfs_rfs_final_report',
] as const

export const ANNOTATION_PATHOLOGY_ONLY_FIELDS = ['case'] as const
